import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import Radio from "../../../components/forms/Radio";
import {
  QuestionWithRelations,
  QuestionOptionWithRelations,
} from "../../../../../api/src/types";
import { useGetCurrentUserQuery } from "../../../api/users";
import {
  useCreateAnswerMutation,
  useUpdateAnswerMutation,
} from "../../../api/answers";
import { useGetQuestionOptionVisibilityQuery } from "../../../api/questionnaire";
import {
  /*IconContainer, */ ErrorWrapper,
  InputWrapper,
} from "./SharedFormStyles";
import useIsMobile from "../../../hooks/useIsMobile";
import Loader from "../../../components/icons/Loader";
import { useSelectedOptions } from "../utils/SelectedOptionsContext";

export const RadioContainer = styled.div`
  flex: 1;
`;

interface RadioInputProps {
  question: QuestionWithRelations;
}

const RadioInput: React.FC<RadioInputProps> = ({ question }) => {
  const isMobile = useIsMobile();
  const { data: user } = useGetCurrentUserQuery();
  const { setSelectedOptionIds } = useSelectedOptions();
  const [selectedOption, setSelectedOption] = useState<string | null>(null);

  const [hasAnswer, setHasAnswer] = useState(question.answers.length > 0);

  /* id of the option whose save is in-flight */
  // const [pendingId, setPendingId] = useState<string | null>(null);
  const [errorId, setErrorId] = useState<string | null>(null);

  const [
    createAnswer,
    {
      /*isLoading: isCreating, */ isError: isCreateError,
      reset: resetCreateAnswer,
    },
  ] = useCreateAnswerMutation();
  const [
    updateAnswer,
    {
      /*isLoading: isUpdating, */ isError: isUpdateError,
      reset: resetUpdateAnswer,
    },
  ] = useUpdateAnswerMutation();

  // Update selectedOption when question changes or answers change
  useEffect(() => {
    const currentSelectedOption =
      question.answers[0]?.answerQuestionOptions.map(
        (a) => a.questionOptionId,
      )[0] ?? null;

    setSelectedOption(currentSelectedOption);
    setHasAnswer(question.answers.length > 0);
  }, [question]);

  // get the ids of the options that are restricted
  const restrictedOptionIds = question.questionOptions
    .filter((option) =>
      option.restrictedQuestionOptions.some(
        (o) => o.restrictedQuestionOptionId === option.id,
      ),
    )
    .map((option) => option.id);

  // get the visibility status of the restricted options
  const { data: visibilityData, isLoading: isVisibilityLoading } =
    useGetQuestionOptionVisibilityQuery(
      {
        restrictedQuestionOptionIds: restrictedOptionIds,
        userId: user?.id ?? "",
      },
      {
        skip: !user?.id || restrictedOptionIds.length === 0,
      },
    );

  const save = async (optionId: string) => {
    resetCreateAnswer();
    resetUpdateAnswer();

    if (!user) return;
    setErrorId(null);
    // setPendingId(optionId);

    const payload = {
      questionId: question.id,
      userId: user.id,
      questionOptionIds: [optionId],
    };

    try {
      if (!hasAnswer) {
        if (optionId) {
          await createAnswer(payload).unwrap();
          setHasAnswer(true);
        }
      } else {
        await updateAnswer(payload).unwrap();
      }
    } catch (err) {
      console.error(err);
      setErrorId(optionId);
    } /* finally {
      setPendingId(null);
    } */
  };

  // Handle radio selection
  const handleRadioChange = (optionId: string) => {
    setSelectedOption(optionId);
    setSelectedOptionIds([optionId]); // Update context for instant protip display
    void save(optionId);
  };

  return (
    <>
      {question.questionOptions.map((option: QuestionOptionWithRelations) => {
        // const isLoading = pendingId === option.id && (isCreating || isUpdating);
        const hasError =
          errorId === option.id && (isUpdateError || isCreateError); // local error

        const isRestricted = restrictedOptionIds.includes(option.id);
        const isVisible = isRestricted
          ? isVisibilityLoading
            ? "loading"
            : (visibilityData?.[option.id] ?? false)
          : true;

        if (isVisible === "loading") {
          return (
            <InputWrapper key={option.id}>
              <RadioContainer>
                <Loader spinnerOnly={true} size={isMobile ? 16 : 34} />
              </RadioContainer>
            </InputWrapper>
          );
        }

        if (!isVisible) return null;

        return (
          <React.Fragment key={option.id}>
            <InputWrapper>
              <RadioContainer>
                <Radio
                  label={option.value || ""}
                  id={option.id}
                  isChecked={selectedOption === option.id}
                  onToggle={() => {
                    handleRadioChange(option.id);
                  }}
                />
              </RadioContainer>
              {/* Commenting out the loader for now as per the request of the design team */}
              {/* {isLoading && (
                <IconContainer>
                  <Loader spinnerOnly={true} size={isMobile ? 16 : 34} />
                </IconContainer>
              )} */}
            </InputWrapper>
            {hasError && (
              <ErrorWrapper>
                Error saving answer, please refresh the page and try again
              </ErrorWrapper>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

export default RadioInput;
